import {
  type CampaignCreationTask,
  CampaignCreationTaskListResponseSchema,
  CampaignCreationTaskReconcileInputSchema,
  CampaignCreationTaskResponseSchema,
  CampaignCreationTaskRetryInputSchema,
  type CampaignCreationTaskSubmissionInput,
  CampaignCreationTaskSubmissionInputSchema,
} from "@adpilot/shared";
import type { QueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "@/lib/api";
import { subscribeToSseInvalidation } from "@/lib/sse-invalidation";

export const CAMPAIGN_CREATION_TASK_QUERY_KEY = ["campaign-creation-tasks"] as const;

export interface CampaignCreationTaskScope {
  userId: string;
  workspaceId: string;
  accessFingerprint: string;
}

export const campaignCreationTaskScopeKey = (scope: CampaignCreationTaskScope | null) =>
  [...CAMPAIGN_CREATION_TASK_QUERY_KEY, scope] as const;

export const campaignCreationTasksKey = (scope: CampaignCreationTaskScope | null) =>
  [...campaignCreationTaskScopeKey(scope), "list"] as const;

export const campaignCreationTaskKey = (
  scope: CampaignCreationTaskScope | null,
  shopId: string | null,
  taskId: string,
) => [...campaignCreationTaskScopeKey(scope), "detail", shopId, taskId] as const;

export function campaignCreationTaskStageKey(task: CampaignCreationTask): string {
  if (["blocked", "needs_attention", "failed"].includes(task.status)) return "needsAttention";
  if (task.status === "canceled") return "canceled";
  if (task.status === "simulated") return "simulated";
  if (task.status === "completed") {
    if (task.executionMode !== "amazon") return "simulated";
    return task.identityVerification === "verified" && task.managedGroupId
      ? "completed"
      : "created";
  }
  if (task.status === "queued") return "phases.accepted";
  if (task.phase !== "compensation" && task.lastError?.recovery === "retry") return "retrying";
  if (task.lastError?.recovery === "reconcile") return "reconciling";
  return `phases.${task.phase}`;
}

const changedEventSchema = z
  .object({
    taskId: z.string().uuid(),
    shopId: z.string().uuid(),
    revision: z.number().int().positive(),
  })
  .strict();

export async function fetchCampaignCreationTasks(signal?: AbortSignal) {
  const response = await api
    .get("campaign-creation/tasks", { searchParams: { pageSize: 100 }, signal })
    .json<unknown>();
  return CampaignCreationTaskListResponseSchema.parse(response);
}

export async function fetchCampaignCreationTask(
  shopId: string | undefined,
  taskId: string,
  signal?: AbortSignal,
): Promise<CampaignCreationTask> {
  const response = await api
    .get(`campaign-creation/tasks/${taskId}`, {
      ...(shopId
        ? { headers: { "X-Shop-Id": shopId }, searchParams: { shopId } }
        : {
            hooks: { beforeRequest: [(request: Request) => request.headers.delete("X-Shop-Id")] },
          }),
      signal,
    })
    .json<unknown>();
  return CampaignCreationTaskResponseSchema.parse(response).data;
}

export async function submitCampaignCreationTask(
  shopId: string,
  input: CampaignCreationTaskSubmissionInput,
): Promise<CampaignCreationTask> {
  const response = await api
    .post("campaign-creation/tasks", {
      headers: { "X-Shop-Id": shopId },
      json: CampaignCreationTaskSubmissionInputSchema.parse(input),
      retry: 0,
    })
    .json<unknown>();
  return CampaignCreationTaskResponseSchema.parse(response).data;
}

export async function retryCampaignCreationTask(
  shopId: string,
  taskId: string,
  input: z.infer<typeof CampaignCreationTaskRetryInputSchema>,
): Promise<CampaignCreationTask> {
  const response = await api
    .post(`campaign-creation/tasks/${taskId}/retry`, {
      headers: { "X-Shop-Id": shopId },
      searchParams: { shopId },
      json: CampaignCreationTaskRetryInputSchema.parse(input),
      retry: 0,
    })
    .json<unknown>();
  return CampaignCreationTaskResponseSchema.parse(response).data;
}

export async function reconcileCampaignCreationTask(
  shopId: string,
  taskId: string,
  input: z.infer<typeof CampaignCreationTaskReconcileInputSchema>,
): Promise<CampaignCreationTask> {
  const response = await api
    .post(`campaign-creation/tasks/${taskId}/reconcile`, {
      headers: { "X-Shop-Id": shopId },
      searchParams: { shopId },
      json: CampaignCreationTaskReconcileInputSchema.parse(input),
      retry: 0,
    })
    .json<unknown>();
  return CampaignCreationTaskResponseSchema.parse(response).data;
}

export function subscribeToCampaignCreationTasks(
  client: QueryClient,
  scope: CampaignCreationTaskScope,
  onConnectionChange: (state: "open" | "error") => void,
) {
  return subscribeToSseInvalidation(client, {
    id: `campaign-creation-tasks:${JSON.stringify(scope)}`,
    url: `/api/v1/campaign-creation/tasks/events?workspaceId=${encodeURIComponent(scope.workspaceId)}`,
    eventNames: ["campaign_creation.task.changed"],
    queryKey: campaignCreationTasksKey(scope),
    onConnectionChange,
    shouldInvalidate: (event) => {
      if (!(event instanceof MessageEvent) || typeof event.data !== "string") return false;
      try {
        const parsed = changedEventSchema.safeParse(JSON.parse(event.data));
        if (!parsed.success) return false;
        void client.invalidateQueries({
          queryKey: campaignCreationTaskKey(scope, parsed.data.shopId, parsed.data.taskId),
          exact: true,
        });
        void client.invalidateQueries({
          queryKey: campaignCreationTaskKey(scope, null, parsed.data.taskId),
          exact: true,
        });
        return true;
      } catch {
        return false;
      }
    },
  });
}
